import { API_BASE_URL, getAuthToken } from '../utils/api';

const base = `${API_BASE_URL}/communications`;

function headers() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getAuthToken()}`,
  };
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      msg = body?.message || msg;
    } catch {}
    throw new Error(msg);
  }
  return res.json();
}

export interface ConversationSummary {
  id: string;
  type: 'INTERNAL' | 'CLIENT' | 'EMAIL';
  subject?: string | null;
  client?: {
    id: string;
    name: string;
    email?: string | null;
    companyName?: string | null;
  } | null;
  createdBy?: { id: string; name: string; email: string };
  participants: Array<{
    id: string;
    userId?: string | null;
    emailAddress?: string | null;
    role?: string | null;
    user?: { id: string; name: string; email: string; profilePhotoUrl?: string | null };
  }>;
  lastMessage: ConversationMessage | null;
  lastMessageAt?: string | null;
  unreadCount: number;
  createdAt: string;
}

export interface ConversationMessage {
  id: string;
  conversationId: string;
  senderUserId?: string | null;
  senderName?: string | null;
  senderEmail?: string | null;
  channel: 'IN_APP' | 'EMAIL' | 'SMS';
  body: string;
  attachments?: any[];
  createdAt: string;
  sender?: { id: string; name: string; email: string; profilePhotoUrl?: string | null } | null;
}

export interface ConversationDetail extends Omit<ConversationSummary, 'lastMessage' | 'unreadCount'> {
  messages: ConversationMessage[];
}

export interface ContactDirectory {
  team: Array<{ id: string; name: string; email: string; role: string; profilePhotoUrl?: string | null }>;
  clients: Array<{ id: string; name: string; companyName?: string | null; email?: string | null }>;
}

export const communicationsService = {
  listConversations(filter: { type?: string; clientId?: string } = {}): Promise<ConversationSummary[]> {
    const qs = new URLSearchParams();
    if (filter.type) qs.set('type', filter.type);
    if (filter.clientId) qs.set('clientId', filter.clientId);
    return fetch(`${base}/conversations?${qs.toString()}`, { headers: headers() }).then(
      (res) => handle<ConversationSummary[]>(res),
    );
  },

  getConversation(id: string): Promise<ConversationDetail> {
    return fetch(`${base}/conversations/${id}`, { headers: headers() }).then(
      (res) => handle<ConversationDetail>(res),
    );
  },

  createConversation(input: {
    type?: 'INTERNAL' | 'CLIENT' | 'EMAIL';
    subject?: string;
    clientId?: string | null;
    participantUserIds?: string[];
    participantEmails?: string[];
    initialMessage?: string;
  }): Promise<ConversationDetail> {
    return fetch(`${base}/conversations`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify(input),
    }).then((res) => handle<ConversationDetail>(res));
  },

  sendMessage(conversationId: string, body: string, channel: 'IN_APP' | 'EMAIL' = 'IN_APP'): Promise<ConversationMessage> {
    return fetch(`${base}/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ body, channel }),
    }).then((res) => handle<ConversationMessage>(res));
  },

  addParticipants(conversationId: string, userIds: string[]): Promise<{ added: number }> {
    return fetch(`${base}/conversations/${conversationId}/participants`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ userIds }),
    }).then((res) => handle<{ added: number }>(res));
  },

  closeConversation(conversationId: string): Promise<any> {
    return fetch(`${base}/conversations/${conversationId}`, {
      method: 'DELETE',
      headers: headers(),
    }).then((res) => handle<any>(res));
  },

  getContacts(): Promise<ContactDirectory> {
    return fetch(`${base}/contacts`, { headers: headers() }).then(
      (res) => handle<ContactDirectory>(res),
    );
  },
};
